import { useCallback, useMemo, useState } from 'react';
import {
  ColumnFiltersState,
  ColumnOrderState,
  ColumnSizingState,
  ExpandedState,
  GroupingState,
  OnChangeFn,
  RowSelectionState,
  SortingState,
  VisibilityState,
} from '@tanstack/react-table';
import { TableSettings } from '@paradigm/design-system/src/components/Table';
import { LeaderboardColumn } from './LeaderboardTable';

export type ColumnSortingDirection = Record<string, 'asc' | 'desc' | undefined>;

export default function useSettings<T>(columns: LeaderboardColumn<T>[]) {
  const defaultSorting = useMemo(() => {
    const defaultColumn = columns.find((c) => c.isDefaultSorting);
    if (defaultColumn?.id == null) return [];

    const { id, sortingDirection } = defaultColumn;
    return [{ id, desc: sortingDirection === 'desc' }];
  }, [columns]);
  const columnSortingDirection = useMemo(
    () => Object.fromEntries(columns.map((c) => [c.id, c.sortingDirection])),
    [columns],
  );

  const selected = useState<RowSelectionState>({});
  const [sortingValue, setSortingValue] =
    useState<SortingState>(defaultSorting);
  const grouping = useState<GroupingState>([]);
  const filtering = useState<ColumnFiltersState>([]);
  const searching = useState<string>('');
  const ordering = useState<ColumnOrderState>([]);
  const sizing = useState<ColumnSizingState>({});
  const visibility = useState<VisibilityState>({});
  const expanding = useState<ExpandedState>({});

  /**
   * Allow only sorting by a given direction that the user must choose.
   * In the leaderboard table, we are always sorting by a given criteria and each column
   * can only be sorted by one direction.
   */
  const setRestrictedSorting: OnChangeFn<SortingState> = useCallback(
    (valueOrFn) => {
      const value =
        typeof valueOrFn === 'function' ? valueOrFn(sortingValue) : valueOrFn;

      if (value[0] != null) {
        const columnId = value[0].id;
        const direction = columnSortingDirection[columnId];
        if (direction != null) {
          setSortingValue([{ id: columnId, desc: direction === 'desc' }]);
          return;
        }
      }
      setSortingValue(value);
    },
    [columnSortingDirection, sortingValue],
  );
  const sorting = [sortingValue, setRestrictedSorting] as const;

  const settings: TableSettings = {
    searching,
    filtering,
    grouping,
    sorting,
    ordering,
    sizing,
    selected,
    visibility,
    expanding,
    isExpandable: false,
    isVirtualized: false,
    sortSide: 'right',
  };

  return {
    settings,
  };
}
